import StaggeredMenu, { StaggeredMenuItem } from '@/components/ui/staggered-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { oliveData } from '@/data/olives';
import { packagingData } from '@/data/packaging';

type MobileMenuProps = {
  navItems: { path: string; label: string }[];
  buildPath: (path: string) => string;
};

const MobileMenu = ({ navItems, buildPath }: MobileMenuProps) => {
  const { language, setLanguage, t } = useLanguage();

  const buildLocalizedPath = (path: string) => {
    if (!path.includes('#')) {
      return buildPath(path);
    }

    const [base, hash] = path.split('#');
    const localizedBase = buildPath(base);
    return `${localizedBase}#${hash}`;
  };

  const getAnchorHref = (anchor: string) => {
    const base = '/collections';
    const query = language === "en" ? "?lang=en" : "";
    return `${base}/${anchor}${query}`;
  };

  const translate = <T extends { bg: string; en: string }>(value: T) => t(value.bg, value.en);

  const items: StaggeredMenuItem[] = navItems.map((item) => {
    const menuItem: StaggeredMenuItem = {
      label: item.label,
      ariaLabel: t(`Навигирай към ${item.label}`, `Navigate to ${item.label}`),
      link: buildLocalizedPath(item.path),
    };

    if (item.path === '/collections') {
      menuItem.subItems = [
        {
          label: t('Маслини', 'Olives'),
          ariaLabel: t('Маслини', 'Olives'),
          link: '#',
          isHeader: true,
          className: 'text-md font-semibold uppercase tracking-wide text-secondary mt-4',
        },
        {
          label: t('Гръцки', 'Greek'),
          ariaLabel: t('Гръцки', 'Greek'),
          link: '#',
          isHeader: true,
          className: 'text-sm font-bold uppercase text-primary/80 mt-2 pl-1',
        },
        ...oliveData.types
          .filter((type) => type.country === 'greek')
          .map((type) => ({
            label: translate(type.name),
            ariaLabel: translate(type.name),
            link: getAnchorHref(type.id),
            className: 'text-sm pl-4',
          })),
        {
          label: t('Турски', 'Turkish'),
          ariaLabel: t('Турски', 'Turkish'),
          link: '#',
          isHeader: true,
          className: 'text-sm font-bold uppercase text-primary/80 mt-4 pl-2',
        },
        ...oliveData.types
          .filter((type) => type.country === 'turkish')
          .map((type) => ({
            label: translate(type.name),
            ariaLabel: translate(type.name),
            link: getAnchorHref(type.id),
            className: 'text-sm pl-4',
          })),
        {
          label: t('Маслинови масла', 'Olive Oils'),
          ariaLabel: t('Маслинови масла', 'Olive Oils'),
          link: '#',
          isHeader: true,
          className: 'text-md font-semibold uppercase tracking-wide text-secondary mt-6',
        },
        ...oliveData.brands.map((brand) => ({
          label: translate(brand.name),
          ariaLabel: translate(brand.name),
          link: getAnchorHref(brand.id),
          className: 'text-sm pl-4',
        })),
      ];
    }

    if (item.path === '/packaging') {
      menuItem.subItems = packagingData.map((pkg) => ({
        label: translate(pkg.title),
        ariaLabel: translate(pkg.title),
        link: buildPath(`/packaging/${pkg.id}`),
      }));
    }

    return menuItem;
  });

  const socialItems = [
    {
      label: 'Български',
      link: '#bg',
      onClick: () => setLanguage('bg'),
    },
    {
      label: 'English',
      link: '#en',
      onClick: () => setLanguage('en'),
    },
  ];

  return (
    <div className="relative flex items-center justify-end lg:hidden">
      <StaggeredMenu
        position="right"
        items={items}
        socialItems={socialItems}
        displaySocials
        displayItemNumbering={false}
        menuButtonColor="#544023"
        openMenuButtonColor="#0f172a"
        changeMenuColorOnOpen={false}
        colors={['#ffffff', '#f8fafc', '#e2e8f0']}
        logoUrl={language === 'bg' ? 'Logo-BG.png' : 'Logo-EN.png'}
        accentColor="#4338ca"
      />
    </div>
  );
};

export default MobileMenu;
